/**
 * Drizzle ORM + IndexedDB Implementation of INoteRepository
 */
import type { INoteRepository, CustomerNoteRecord } from './INoteRepository';
import { getIndexedDb, STORES } from '../db/db.web';

const SEED_NOTES: CustomerNoteRecord[] = [
  {
    id: 'NOTE-101',
    customerId: 'KH-1001',
    type: 'journal',
    content: 'Khách hàng quan tâm đến quyền lợi nằm viện và bảo vệ thu nhập gia đình.',
    author: 'Nguyễn Văn Cao (FA)',
    date: '20/06/2026',
    createdAt: '2026-06-20T10:00:00.000Z',
    updatedAt: '2026-06-20T10:00:00.000Z',
  },
  {
    id: 'NOTE-102',
    customerId: 'KH-1001',
    type: 'gift',
    content: 'Tặng lịch treo tường và quà tri ân sinh nhật tháng 1.',
    author: 'Nguyễn Văn Cao (FA)',
    date: '24/01/2026',
    createdAt: '2026-01-24T10:00:00.000Z',
    updatedAt: '2026-01-24T10:00:00.000Z',
  },
  {
    id: 'NOTE-103',
    customerId: 'KH-1002',
    type: 'journal',
    content: 'Tư vấn gói giải pháp tích lũy học vấn cho con 15-20 năm.',
    author: 'Nguyễn Văn Cao (FA)',
    date: '22/06/2026',
    createdAt: '2026-06-22T14:30:00.000Z',
    updatedAt: '2026-06-22T14:30:00.000Z',
  },
];

export class NoteRepository implements INoteRepository {
  private initialized = false;
  private memoryStore: CustomerNoteRecord[] = [...SEED_NOTES];

  private async ensureInitialized(): Promise<void> {
    if (this.initialized) return;
    try {
      const db = await getIndexedDb();
      if (db) {
        const count = await db.count(STORES.NOTES);
        if (count === 0) {
          const tx = db.transaction(STORES.NOTES, 'readwrite');
          for (const note of SEED_NOTES) {
            await tx.store.put(note);
          }
          await tx.done;
        }
        this.initialized = true;
      }
    } catch (err) {
      // IndexedDB not available
    }
  }

  async getByCustomerId(customerId: string): Promise<CustomerNoteRecord[]> {
    await this.ensureInitialized();
    try {
      const db = await getIndexedDb();
      const all: CustomerNoteRecord[] = await db.getAll(STORES.NOTES);
      return all.filter((n) => n.customerId === customerId);
    } catch (err) {
      return this.memoryStore.filter((n) => n.customerId === customerId);
    }
  }

  async getAll(): Promise<CustomerNoteRecord[]> {
    await this.ensureInitialized();
    try {
      const db = await getIndexedDb();
      return await db.getAll(STORES.NOTES);
    } catch (err) {
      return this.memoryStore;
    }
  }

  async save(data: Omit<CustomerNoteRecord, 'id' | 'createdAt' | 'updatedAt'>): Promise<CustomerNoteRecord> {
    await this.ensureInitialized();
    const nowIso = new Date().toISOString();

    const newNote: CustomerNoteRecord = {
      ...data,
      id: `NOTE-${Date.now()}`,
      createdAt: nowIso,
      updatedAt: nowIso,
    };

    try {
      const db = await getIndexedDb();
      await db.put(STORES.NOTES, newNote);
    } catch (err) {
      this.memoryStore.push(newNote);
    }

    return newNote;
  }

  async delete(id: string): Promise<boolean> {
    await this.ensureInitialized();
    try {
      const db = await getIndexedDb();
      await db.delete(STORES.NOTES, id);
      return true;
    } catch (err) {
      const idx = this.memoryStore.findIndex((n) => n.id === id);
      if (idx !== -1) {
        this.memoryStore.splice(idx, 1);
        return true;
      }
      return false;
    }
  }
}

export const noteRepository = new NoteRepository();
